import { Controller } from "@hotwired/stimulus";

// The free invoice generator's form: adds and removes lines, works out the totals, and keeps a
// preview of the invoice up to date as the visitor types. The preview uses the same markup and
// styles as the real invoice (koteshen_spark/_document), so what they see is what they download.
const PAPER_WIDTH = 760;

export default class extends Controller {
  static targets = ["form", "items", "template", "paper", "frame", "total", "row", "lineTotal"];
  // The wording around the lines for each document; kept in step with KoteshenSpark::Invoice::KINDS
  static values = { labels: Object };

  connect() {
    this.logoUrl = null;
    this.resizeObserver = new ResizeObserver(() => this.fit());
    this.resizeObserver.observe(this.frameTarget);
    this.render();
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    if (this.logoUrl) URL.revokeObjectURL(this.logoUrl);
  }

  addItem() {
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, Date.now().toString());
    this.itemsTarget.insertAdjacentHTML("beforeend", html);
    this.itemsTarget.lastElementChild.querySelector("input[type=text]")?.focus();
    this.render();
  }

  // A trade page offers its usual line items: tapping one fills the first empty line, or adds a new one
  addPreset(event) {
    const name = event.currentTarget.dataset.name;
    let row = this.rowTargets.find((r) => !r.hidden && !r.querySelector("[data-field='item_name']").value.trim());
    if (!row) {
      this.addItem();
      row = this.rowTargets[this.rowTargets.length - 1];
    }
    row.querySelector("[data-field='item_name']").value = name;
    row.querySelector("[data-field='item_rate']").focus();
    this.render();
  }

  removeItem(event) {
    const row = event.target.closest("[data-free-invoice-target~='row']");
    const destroy = row.querySelector("[data-field='item_destroy']");
    const persisted = row.querySelector("input[name$='[id]']");

    if (persisted) {
      destroy.value = "1";
      row.hidden = true;
    } else if (this.rowTargets.filter((r) => !r.hidden).length > 1) {
      row.remove();
    } else {
      row.querySelectorAll("input[type=text]").forEach((input) => (input.value = ""));
    }
    this.render();
  }

  render() {
    const fields = this.read();
    const items = this.items();
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const money = this.money(fields.currency);

    this.rowTargets.forEach((row) => {
      const target = row.querySelector("[data-free-invoice-target~='lineTotal']");
      if (target) target.textContent = money(this.lineTotal(row));
    });
    if (this.hasTotalTarget) this.totalTarget.textContent = money(subtotal);

    this.paperTarget.innerHTML = this.document(fields, items, subtotal, money);
    this.fit();
  }

  fit() {
    const scale = this.frameTarget.clientWidth / PAPER_WIDTH;
    this.paperTarget.style.width = `${PAPER_WIDTH}px`;
    this.paperTarget.style.transform = `scale(${scale})`;
    this.frameTarget.style.height = `${this.paperTarget.offsetHeight * scale}px`;
  }

  // -- reading the form --

  read() {
    const value = (name) => this.formTarget.querySelector(`[data-field='${name}']`)?.value?.trim() || "";
    const template = this.formTarget.querySelector("[data-field='template']:checked")?.value || "clean";
    const logo = this.formTarget.querySelector("[data-field='business_logo']")?.files?.[0];
    if (logo && logo.name !== this.logoName) {
      if (this.logoUrl) URL.revokeObjectURL(this.logoUrl);
      this.logoUrl = URL.createObjectURL(logo);
      this.logoName = logo.name;
    } else if (!logo) {
      this.logoUrl = null;
      this.logoName = null;
    }

    return {
      template,
      logo: this.logoUrl,
      number: value("number"),
      notes: this.formTarget.querySelector("[data-field='notes']")?.value?.trim() || "",
      pay: this.formTarget.querySelector("[data-field='payment_instructions']")?.value?.trim() || "",
      reference: value("reference"),
      taxId: value("tax_id"),
      paid: this.number(value("amount_paid")),
      currency: value("currency") || "USD",
      issued: value("issued"),
      due: value("due"),
      business: {
        name: value("business_name"),
        email: value("business_email"),
        phone: value("business_phone"),
        address: this.address(value("business_street"), value("business_city"), this.countryName("business_country")),
      },
      customer: {
        name: value("customer_name"),
        email: value("customer_email"),
        address: this.address(value("customer_street"), value("customer_city"), this.countryName("customer_country")),
      },
    };
  }

  items() {
    return this.rowTargets
      .filter((row) => !row.hidden)
      .map((row) => ({
        name: row.querySelector("[data-field='item_name']").value.trim(),
        quantity: row.querySelector("[data-field='item_quantity']").value.trim() || "1",
        rate: row.querySelector("[data-field='item_rate']").value.trim(),
        total: this.lineTotal(row),
        unit: this.number(row.querySelector("[data-field='item_rate']").value),
      }))
      .filter((item) => item.name || item.unit > 0);
  }

  lineTotal(row) {
    return this.number(row.querySelector("[data-field='item_quantity']").value) * this.number(row.querySelector("[data-field='item_rate']").value);
  }

  // Same reading as KoteshenSpark::NumberText on the server: "1,150" and "$1,150.50" have thousands
  // separators, and "12,50" is a decimal comma
  number(text) {
    let clean = String(text).replace(/[^\d.,-]/g, "");
    clean = /,\d{1,2}$/.test(clean) && !clean.includes(".") ? clean.replace(",", ".") : clean.replace(/,/g, "");
    const parsed = parseFloat(clean);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  // Same rule as the printed invoice: a country on its own isn't an address
  address(street, city, country) {
    if (!street && !city) return "";
    return [street, city, country].filter(Boolean).join(", ");
  }

  countryName(field) {
    const select = this.formTarget.querySelector(`[data-field='${field}']`);
    return select?.selectedOptions?.[0]?.textContent?.trim() || "";
  }

  money(currency) {
    let formatter;
    try {
      formatter = new Intl.NumberFormat("en-US", { style: "currency", currency });
    } catch (_) {
      formatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
    }
    return (amount) => formatter.format(amount);
  }

  date(text) {
    if (!text) return "";
    const [year, month, day] = text.split("-").map(Number);
    if (!year) return "";
    return new Date(year, month - 1, day).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
  }

  escape(text) {
    const div = document.createElement("div");
    div.textContent = text ?? "";
    return div.innerHTML;
  }

  // -- the preview, in the same markup as the real invoice --

  document(fields, items, subtotal, money) {
    const e = (text) => this.escape(text);
    const contact = [fields.business.address, fields.business.email, fields.business.phone].filter(Boolean).map(e).join("<br>");
    const billed = [fields.customer.address, fields.customer.email].filter(Boolean).map(e).join("<br>");
    const labels = this.labelsValue;
    const paid = labels.name === "Invoice" ? Math.min(fields.paid, subtotal) : 0;
    const rows = items.length
      ? items
          .map(
            (item) =>
              `<tr><td>${e(item.name)}</td><td class="num">${e(item.quantity)}</td><td class="num">${item.rate ? money(item.unit) : ""}</td><td class="num">${money(item.total)}</td></tr>`,
          )
          .join("")
      : `<tr><td style="color:#8f99a9">Your first line of work will show here</td><td></td><td></td><td></td></tr>`;

    return `
      <article class="kinv kinv--${e(fields.template)}">
        <header class="kinv__head">
          <div class="kinv__brand">
            ${fields.logo ? `<img class="kinv__logo" src="${fields.logo}" alt="">` : ""}
            <p class="kinv__name">${e(fields.business.name) || "Your business name"}</p>
            ${contact ? `<p class="kinv__contact">${contact}</p>` : ""}
            ${fields.taxId ? `<p class="kinv__contact">Tax ID / License: ${e(fields.taxId)}</p>` : ""}
          </div>
          <div class="kinv__title"><p class="kinv__word">${e(labels.name)}</p><p class="kinv__no">${e(fields.number)}</p>${fields.reference ? `<p class="kinv__no">Ref: ${e(fields.reference)}</p>` : ""}</div>
        </header>
        <div class="kinv__body">
          <section class="kinv__meta">
            <div><p class="kinv__label">${e(labels.to)}</p><p class="kinv__value">${e(fields.customer.name) || "Your customer"}</p>${billed ? `<p class="kinv__sub">${billed}</p>` : ""}</div>
            <div><p class="kinv__label">${e(labels.date)}</p><p class="kinv__value">${e(this.date(fields.issued))}</p></div>
            ${labels.expiry ? `<div><p class="kinv__label">${e(labels.expiry)}</p><p class="kinv__value">${e(this.date(fields.due))}</p></div>` : ""}
          </section>
          <table>
            <thead><tr><th>Description</th><th class="num">Qty</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="kinv__totals">
            <div class="kinv__row"><span>Subtotal</span><span>${money(subtotal)}</span></div>
            ${paid > 0 ? `<div class="kinv__row"><span>Already paid</span><span>-${money(paid)}</span></div>` : ""}
            <div class="kinv__row kinv__row--due"><span>${e(labels.total)}</span><span class="kinv__amount">${money(subtotal - paid)}</span></div>
          </div>
        </div>
        ${fields.pay ? `<section class="kinv__notes"><p class="kinv__label">How to pay</p><p class="kinv__sub">${e(fields.pay)}</p></section>` : ""}
        ${fields.notes ? `<section class="kinv__notes"><p class="kinv__label">Notes</p><p class="kinv__sub">${e(fields.notes)}</p></section>` : ""}
        <footer class="kinv__foot"><span>${e(labels.closing)}</span><span>Made with Koteshen</span></footer>
      </article>`;
  }
}
